import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { QrCode, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * QR code for a specific page URL (defaults to current location).
 */
export const QRDialog = ({ url, title, label, triggerClassName, testId = "qr-btn" }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const target = url || (typeof window !== "undefined" ? window.location.href : "");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(target);
      setCopied(true);
      toast.success(t("linkCopied"));
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn("clipboard failed:", err);
      toast.error("Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={triggerClassName || "border-[#993D22] text-[#993D22]"}
          data-testid={testId}
        >
          <QrCode className="w-4 h-4 mr-2" /> {label || "QR"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#FAF7F2] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">
            {title || "QR"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6E5B4F]">
            {t("qrPersonDesc")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center my-4" data-testid="qr-image">
          <div className="p-4 rounded-xl bg-white border-2 border-[#993D22]">
            <QRCodeSVG value={target} size={220} fgColor="#261911" bgColor="#FFFFFF" level="M" />
          </div>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-full border border-[#E3D5CA] p-1.5">
          <code className="flex-1 truncate text-xs text-[#3A2312] px-2" data-testid="qr-url">
            {target}
          </code>
          <Button type="button" size="sm" onClick={copy} className="btn-terra !py-1.5 !px-3" data-testid="qr-copy-btn">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRDialog;
